import { Beam } from "vexflow";
import type { StaveNote } from "vexflow";
import type { ScoreNote } from "./types";

/**
 * Beam grouping, decided from where each note actually falls in the bar.
 *
 * This replaces `Beam.generateBeams(notes, { groups })`. That call asks VexFlow
 * to re-derive the beat grid by counting ticks from the first note, and it loses
 * the grid the moment a note straddles a group boundary. Two bars in this book
 * already hit it:
 *
 *   - `Fun at the Park` bar 11 — dotted crotchet, then two semiquavers in the
 *     second half of beat 2. Those two belong under one beam; VexFlow, having
 *     lost count after the dotted crotchet, flagged them both.
 *   - 6/8 with a dotted crotchet then three quavers: the first quaver came out
 *     orphaned off the beam it belongs to.
 *
 * Every note in a score already carries an absolute `start` in quarter notes, so
 * the grid never has to be guessed.
 *
 * ── THE RULE, taken from Jason's own engraving ──────────────────────────────
 *
 * The printed book is the reference, and it is consistent. `Meet the Flintstones`
 * bar 10 prints four quavers under ONE beam and bar 1 prints a lone quaver with
 * a FLAG followed by a beamed pair. Both come from the same rule, which is also
 * what Gould prescribes and what MuseScore does:
 *
 *   A half bar is beamed as ONE group when every beat inside it is filled edge
 *   to edge with quavers. The moment a rest, a longer note or a shorter note
 *   appears in that half bar, the beams cut back to ONE BEAT, so the beam keeps
 *   showing the reader where the beat is.
 *
 * Everything else follows from that:
 *
 *  - Semiquavers therefore always beam by the beat, because a beat containing
 *    them is never "filled edge to edge with quavers".
 *  - Compound time beams by the dotted beat: 6/8 as 3+3, 9/8 as 3+3+3.
 *  - Irregular metres take their traditional grouping: 7/8 as 2+2+3, 5/8 as 2+3.
 *  - A rest breaks a beam. A single note left on its own gets a FLAG, never a
 *    beam of one — the difference between a quaver beam and a quaver stem, and
 *    the thing a reader notices first.
 *  - A beam never crosses a barline.
 *
 * A pickup bar is measured backwards from the downbeat that follows it, so the
 * grid in a short first bar still lines up with the beats being counted.
 */

/** Note values that can carry a beam at all. */
const BEAMABLE = new Set(["eighth", "16th", "32nd"]);
const EPS = 1e-9;

/** Quarter-note length of one printed beat. */
function beatLength(time: [number, number]): number {
  return 4 / time[1];
}

/** Beat cells and half-bar cells for a metre, in quarter notes. */
function gridPattern(time: [number, number]): { beat: number[]; wide: number[] } {
  const [top, bottom] = time;
  const key = `${top}/${bottom}`;

  // Irregular metres carry their own traditional grouping and never widen.
  if (key === "7/8") return { beat: [1, 1, 1.5], wide: [1, 1, 1.5] };
  if (key === "5/8") return { beat: [1, 1.5], wide: [1, 1.5] };
  if (key === "8/8") return { beat: [1.5, 1.5, 1], wide: [1.5, 1.5, 1] };

  // Compound time: the printed beat IS the dotted note, so both grids agree.
  if (bottom === 8 && top % 3 === 0) return { beat: [1.5], wide: [1.5] };

  const beat = beatLength(time);
  const barQuarters = top * beat;
  const half = barQuarters / 2;
  // Only 4/4-shaped bars have a half worth widening to; in 2/4 and 2/2 the half
  // bar IS one beat already.
  const canWiden = top % 2 === 0 && half > beat + EPS;
  return { beat: [beat], wide: canWiden ? [half] : [beat] };
}

/** Cell boundaries covering `length` quarter notes, from a repeating pattern. */
function boundaries(pattern: number[], length: number): number[] {
  const out = [0];
  let at = 0;
  let i = 0;
  let guard = 0;
  while (at < length - EPS && guard++ < 512) {
    at += pattern[i % pattern.length];
    i += 1;
    out.push(at);
  }
  return out;
}

export interface BeamEntry {
  note: StaveNote;
  head: ScoreNote;
}

export interface BeamBarContext {
  /** Absolute quarter-note position of this bar's downbeat. */
  barStart: number;
  /** Full length of a bar in this metre, in quarter notes. */
  barQuarters: number;
  time: [number, number];
}

interface Span { e: BeamEntry; from: number; to: number }

/** Runs of consecutive beamable notes inside one cell, each ≥2 notes long. */
function runsIn(spans: Span[], lo: number, hi: number): Span[][] {
  const out: Span[][] = [];
  let run: Span[] = [];
  for (const s of spans) {
    const inside = s.from >= lo - EPS && s.to <= hi + EPS;
    const beamable = !s.e.head.rest && BEAMABLE.has(s.e.head.d);
    if (!inside) continue;
    if (!beamable) {
      if (run.length > 1) out.push(run);
      run = [];
      continue;
    }
    run.push(s);
  }
  if (run.length > 1) out.push(run);
  return out;
}

/** True when this cell is filled edge to edge with plain quavers. */
function solidQuavers(spans: Span[], lo: number, hi: number): boolean {
  const inside = spans.filter((s) => s.from >= lo - EPS && s.to <= hi + EPS);
  if (inside.length < 2) return false;
  if (inside.some((s) => s.e.head.rest || s.e.head.d !== "eighth")) return false;
  // No gap at either end and none in the middle.
  if (Math.abs(inside[0].from - lo) > EPS) return false;
  if (Math.abs(inside[inside.length - 1].to - hi) > EPS) return false;
  for (let i = 1; i < inside.length; i++) {
    if (Math.abs(inside[i].from - inside[i - 1].to) > EPS) return false;
  }
  // And nothing else pokes into the cell from outside it.
  const overlapsOutside = spans.some(
    (s) => !inside.includes(s) && s.to > lo + EPS && s.from < hi - EPS,
  );
  return !overlapsOutside;
}

/**
 * Build the beams for ONE bar of ONE hand.
 *
 * Entries must be in sounding order and must already be VexFlow notes, so the
 * beams hold the same objects the voice draws.
 */
export function beamsForBar(entries: BeamEntry[], ctx: BeamBarContext): Beam[] {
  if (entries.length < 2) return [];

  const { beat, wide } = gridPattern(ctx.time);
  const beatBounds = boundaries(beat, ctx.barQuarters);
  const wideBounds = boundaries(wide, ctx.barQuarters);

  // Offsets are measured from the bar's own downbeat. A short first bar is a
  // pickup: shift it so it ENDS on the downbeat that follows, which is the grid
  // the reader is actually counting against.
  const spans: Span[] = entries.map((e) => ({
    e,
    from: e.head.start - ctx.barStart,
    to: e.head.start - ctx.barStart + e.head.dur,
  }));
  const sung = spans[spans.length - 1].to;
  const shift = sung < ctx.barQuarters - EPS ? ctx.barQuarters - sung : 0;
  if (shift) for (const s of spans) { s.from += shift; s.to += shift; }

  const beams: Beam[] = [];
  for (let w = 0; w < wideBounds.length - 1; w++) {
    const lo = wideBounds[w];
    const hi = wideBounds[w + 1];

    if (solidQuavers(spans, lo, hi)) {
      const all = spans.filter((s) => s.from >= lo - EPS && s.to <= hi + EPS);
      // `true` = let VexFlow pick ONE direction for the whole group from the
      // note furthest from the middle line, which is the rule for a beam.
      // Without it the group keeps each note's own stem and the beam bends
      // through the middle of the notes.
      beams.push(new Beam(all.map((s) => s.e.note), true));
      continue;
    }

    // Cut back to the beat, so the beam still shows where the beat falls.
    for (let b = 0; b < beatBounds.length - 1; b++) {
      const blo = beatBounds[b];
      const bhi = beatBounds[b + 1];
      if (blo < lo - EPS || bhi > hi + EPS) continue;
      for (const run of runsIn(spans, blo, bhi)) {
        beams.push(new Beam(run.map((s) => s.e.note), true));
      }
    }
  }

  return beams;
}
